/* A native dialog using the existing gallery's Shopify photo links as its source. */
if (!customElements.get('product-image-viewer')) {
  customElements.define('product-image-viewer', class extends HTMLElement {
    connectedCallback() {
      this.lifecycle?.abort();
      this.lifecycle = new AbortController();
      const options = { signal: this.lifecycle.signal };
      this.dialog = this.querySelector('dialog');
      this.track = this.querySelector('[data-inspector-track]');
      this.product = this.closest('product-detail');
      this.product.addEventListener('click', event => {
        const link = event.target.closest('[data-inspect-image]');
        if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.open(link);
      }, options);
      this.addEventListener('click', event => {
        if (event.target.closest('[data-inspector-close]')) this.dialog.close();
        const step = event.target.closest('[data-inspector-step]');
        if (step) this.goTo(this.active + Number(step.dataset.inspectorStep));
      }, options);
      this.dialog.addEventListener('keydown', event => {
        if (event.key === 'Tab') {
          const controls = [...this.dialog.querySelectorAll('button:not(:disabled), [tabindex="0"]')].filter(control => !control.hidden);
          const first = controls[0], last = controls.at(-1);
          if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
          else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
          return;
        }
        const rtl = getComputedStyle(this.track).direction === 'rtl';
        const step = event.key === 'ArrowRight' ? (rtl ? -1 : 1) : event.key === 'ArrowLeft' ? (rtl ? 1 : -1) : 0;
        if (step) { event.preventDefault(); this.goTo(this.active + step); }
        if (event.key === 'Home' || event.key === 'End') {
          event.preventDefault(); this.goTo(event.key === 'Home' ? 0 : this.photos.length - 1);
        }
      }, options);
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.update());
      }, { ...options, passive: true });
      this.dialog.addEventListener('close', () => {
        cancelAnimationFrame(this.frame);
        // A resize while inspecting may have moved the original gallery slide.
        const gallery = this.trigger?.closest('product-gallery');
        if (gallery && this.trigger.isConnected) {
          gallery.goTo(gallery.slides.indexOf(this.trigger.closest('.pdp-gallery__slide')), 'instant');
          gallery.update();
          this.trigger.focus({ preventScroll: true });
        }
      }, options);
      this.resize = new ResizeObserver(() => {
        if (this.dialog.open) this.goTo(this.active, 'instant');
      });
      this.resize.observe(this.track);
    }

    open(link) {
      if (this.dialog.open) return;
      this.trigger = link;
      this.photos = [...this.product.querySelectorAll('[data-inspect-image]')];
      this.active = this.photos.indexOf(link);
      const slides = this.photos.map((photo, index) => {
        const slide = document.createElement('div');
        slide.className = 'pdp-inspector__slide';
        slide.setAttribute('role', 'group');
        slide.setAttribute('aria-label', `${index + 1} / ${this.photos.length}`);
        const image = document.createElement('img');
        image.alt = photo.querySelector('img').alt;
        image.width = Number(photo.dataset.imageWidth);
        image.height = Number(photo.dataset.imageHeight);
        image.decoding = 'async';
        slide.append(image);
        return slide;
      });
      this.track.replaceChildren(...slides);
      this.querySelectorAll('[data-inspector-step]').forEach(button => { button.hidden = this.photos.length < 2; });
      this.dialog.showModal();
      this.goTo(this.active, 'instant');
      this.update();
      this.querySelector('[data-inspector-close]').focus({ preventScroll: true });
    }

    goTo(index, behavior) {
      index = Math.max(0, Math.min(index, this.photos.length - 1));
      const rtl = getComputedStyle(this.track).direction === 'rtl';
      this.track.scrollTo({ left: index * this.track.clientWidth * (rtl ? -1 : 1), behavior: behavior || (matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth') });
    }

    update() {
      if (!this.dialog.open) return;
      this.active = Math.min(this.photos.length - 1, Math.round(Math.abs(this.track.scrollLeft) / (this.track.clientWidth || 1)));
      [...this.track.children].forEach((slide, index) => {
        slide.setAttribute('aria-hidden', String(index !== this.active));
        const image = slide.firstElementChild;
        // Request only the inspected image and its immediate neighbors, never all eagerly.
        if (Math.abs(index - this.active) <= 1 && !image.hasAttribute('src')) image.src = this.photos[index].href;
      });
      this.querySelector('[data-inspector-status]').textContent = `${this.active + 1} / ${this.photos.length}`;
      this.querySelector('[data-inspector-step="-1"]').disabled = this.active === 0;
      this.querySelector('[data-inspector-step="1"]').disabled = this.active === this.photos.length - 1;
    }

    disconnectedCallback() {
      this.lifecycle?.abort();
      this.resize?.disconnect();
      cancelAnimationFrame(this.frame);
      if (this.dialog?.open) this.dialog.close();
    }
  });
}
